import { DateTime, IANAZone } from "luxon";
import { repairMissingIndexes, syncIndex, toDenseArray } from "../../shared/utils/utils";
import { TimePeriod } from "../time-period.class";
import { OptionTypeEnum } from "./option-type.class";
import { OptionClass, OptionData, OptionPersistence } from "./option.class";
import isEqual from 'lodash/isEqual'

export type OptionListPersistence = OptionPersistence[] | OptionListPersistenceNew | null

export interface OptionListPersistenceNew {
    sortMap: number[];
    optionArray: OptionPersistence[];
}
const optionDataDummy: OptionData = {
    index: 0,
    optionConfigKey: 'dummy',
    isDeleted: true,
    type: OptionTypeEnum.text
}
/**
 * Class to handle with collection of options for a poll
 * It supports two kind of indezies:
 * 1.) the option-index which should never be changed because the votes are linked via this index
 * 2.) the sort-index that is the sort order in which  options are displayed
 * forEach and the iterator are considering the sort-order and exlcuding option that are marked as deleted
 * to access options in *ngFor us .orderOptions
 * To persist options use the optionData of OptionClass
 */
export class OptionList implements Iterable<OptionClass> {
    private _optionClassArray: OptionClass[] = [];
    private _optionsSinceLastSave: OptionClass[] = [];
    private _islegacyData: boolean = false /**indicates if  sortmap should persist  and options persist as optionArray */
    private _sortMap: number[] = [];
    private _sortMapSinceLastSave: number[] = [];
    private _timeZone: string
    private _wasHealed: boolean = false;

    constructor(
        data: OptionListPersistence,
        timeZone: string
    ) {
        this._timeZone = timeZone
        let optionPersistanceArray: OptionPersistence[] = []
        if (data) {// @todo: make this more beautifull
            if (OptionList.isOfTypeNew(data)) { // due to legacy data we have to distingush these cases
                // a node without optionArray (sortMap only, empty node) is a poll without options (rules-hardening-1)
                optionPersistanceArray = (<OptionListPersistenceNew>data).optionArray ?? [];
                if ((<OptionListPersistenceNew>data).sortMap) this._sortMap = (<OptionListPersistenceNew>data).sortMap;
            } else {// legacy
                optionPersistanceArray = (<OptionPersistence[]>data);
                this._islegacyData = true;
            }
        }
        if (!data) return;
        if (Array.isArray(optionPersistanceArray) && optionPersistanceArray.length === 0) {
            // no options: nothing to repair - repairMissingIndexes would add a dummy for index 0 and report a
            // healing; a sortMap without options points nowhere
            this._sortMap = [];
            this.resetLastSave()
        } else {
            // firebase delivers sparse numeric nodes as a map instead of an array
            const isMapForm = !Array.isArray(optionPersistanceArray)
            if (isMapForm) {
                optionPersistanceArray = toDenseArray(optionPersistanceArray, optionDataDummy)
            }
            const isArrayCorrupt = repairMissingIndexes(optionPersistanceArray, optionDataDummy)
            const isIndexCorrupt = syncIndex(optionPersistanceArray, 'index')
            this.populateOptionClassArray(optionPersistanceArray);
            if (this.isSortMapCorrupt || isMapForm || isArrayCorrupt || isIndexCorrupt) {
                this.resetSortMap()
                // legacy data never persists a sortMap, so there is nothing to heal there;
                // an empty list needs no repair either
                this._wasHealed = !this._islegacyData && this._optionClassArray.length > 0
            }
            this.resetLastSave()
        }
    }

    /**
     * true if the constructor had to repair the persisted data (sortMap or optionArray).
     * The repair only happened in-memory - the owner should persist the sortMap
     * so other clients don't run into the same corruption again.
     */
    get wasHealed(): boolean {
        return this._wasHealed
    }

    get timeZone() {
        return this._timeZone
    }
    set timeZone(value: string) {
        if (!IANAZone.isValidZone(value)) throw new Error('OptionList: Not a valid timeZone: ' + value)
        this._timeZone = value
    }
    /**
     * content based validation: the sortMap must contain exactly the indexes of all
     * not deleted options - no out-of-range entries, no entries pointing to deleted
     * options, no duplicates. With the length matching, "all valid, distinct and not
     * deleted" implies that no not-deleted option is missing.
     */
    private get isSortMapCorrupt(): boolean {
        if (!this._sortMap) return true
        if (!Array.isArray(this._sortMap)) return true
        if (this._sortMap.length !== this.numberOfNotDeletedOptions) return true
        const seenOptionIndexes = new Set<number>()
        for (const optionIndex of this._sortMap) {
            const option = this._optionClassArray[optionIndex]
            if (!option) return true // out of range or gap
            if (option.isDeleted) return true // points to a deleted option
            if (seenOptionIndexes.has(optionIndex)) return true // duplicate
            seenOptionIndexes.add(optionIndex)
        }
        return false
    }

    private get numberOfNotDeletedOptions(): number {
        let returnValue = this._optionClassArray.length
        this._optionClassArray.forEach(option => {
            if (option.isDeleted) returnValue--
        })
        return returnValue
    }

    private populateOptionClassArray(optionDataArray: OptionPersistence[]) {
        optionDataArray?.forEach((optionPersistence, index) => {
            this._optionClassArray.push(new OptionClass(optionPersistence, this._timeZone));
        });
    }

    /**
     *
     * @returns a new option list with a deep copy of the options
     * keeps the order of the sortmap
     * options that are marked as deleted are not copied
     */

    deepCopy(): OptionList {
        const returnValue = new OptionList(null, this._timeZone)
        this.forEach((option, index) => {
            const optionCoopy = option.deepCopy()
            returnValue.add(optionCoopy, index)

        })
        return returnValue
    }

    /**
     * Legacy is the flat structure options/{o}/{p}/{i}: a numeric child key. Everything else is the new structure
     * (optionArray + sortMap) - also a node with a sortMap only, an empty node and null. Until rules-hardening-1 the
     * check was "optionArray is truthy", which loaded such nodes as legacy and wrote every new option flat
     * (report-database-rules-0-audit §2 B7). A node with both (one poll in prod) stays legacy and readable.
     */
    static isOfTypeNew(data: OptionData[] | OptionListPersistenceNew) {
        if (!data || typeof data !== 'object') return true;
        return !Object.keys(data).some(key => /^\d+$/.test(key));
    }

    public uninitialize() {
        this._optionClassArray = [];
        this._optionsSinceLastSave = [];
        this._sortMapSinceLastSave = [];
        this._islegacyData = false
        this._sortMap = [];
        this._wasHealed = false;
    }

    /**
     *  excluding deleted option use this to iterate in *ngFor
    */
    get orderedOptions(): OptionClass[] {
        // console.warn('orderedOptions')
        const returnValue: OptionClass[] = [] // @todo don't create a new array all the time - fill array of OptionClass on init sintead of data...
        this.forEach(optionClass => {
            returnValue.push(optionClass)
        })
        return returnValue
    }

    get isLegacyData(): boolean {
        return this._islegacyData
    }

    get sortMap(): number[] {
        return this._sortMap
    }

    private resetSortMap() {
        const optionsArrayCopy = [...this._optionClassArray]
        optionsArrayCopy.sort(OptionClass.standardCompare)
        this._sortMap = []
        optionsArrayCopy.forEach((option) => {
            if (!option.isDeleted) {
                this._sortMap.push(option.index)
            }
        })
    }

    /**
     * considering only NOT deleted options
     */
    get length(): number {
        let returnValue = 0
        this.forEach(value => {
            returnValue++
        })
        return returnValue
    }


    get lengthIncludingDeleted(): number {
        return this._optionClassArray.length
    }

    get firstCheckedOption(): OptionClass {
        return this.orderedOptions.find(
            (option) => option.checked === true
        );
    }

    /**loop in the sort order and jump over deleted options */
    forEach(callback: (option: OptionClass, index: number) => void) {
        if (!this._sortMap) return
        for (let i = 0; i < this._sortMap.length; i++) {
            const optionClass = this._optionClassArray[this._sortMap[i]]
            if (!optionClass) {
                // no poll content in the log - only indexes and lengths
                console.error('OptionList.forEach: sortMap entry points to missing option', {
                    sortIndex: i,
                    optionIndex: this._sortMap[i],
                    sortMapLength: this._sortMap.length,
                    optionArrayLength: this._optionClassArray.length,
                })
            } else {
                if (optionClass && !optionClass.isDeleted) {// the deletion check is not required because deleted option should not be in the sortMap
                    callback(optionClass, i);
                }
            }
        }
    }

    /**loop in the option-index order including deleted options */
    forEachIncludingDeleted(callback: (option: OptionClass, index: number) => void) {
        for (let i = 0; i < this._optionClassArray.length; i++) {
            const optionClass = this._optionClassArray[i]
            {
                callback(optionClass, i);
            }
        }
    }

    /** loop in the sort order and jump over deleted options */
    [Symbol.iterator](): Iterator<OptionClass> {
        let index = 0;
        return {
            next: () => {
                for (let i = 0; i < this._sortMap.length; i++) {
                    const optionClass = this._optionClassArray[this._sortMap[i]]
                    if (optionClass && !optionClass.isDeleted) {
                        return { value: optionClass, done: false };
                    }
                }
                return { done: true, value: null };
            }
        };
    }

    public markCheckedOptionsAsDeleted() {
        const checkedOptions: OptionClass[] = []
        this.forEach((option) => {
            if (option.checked === true) {
                checkedOptions.push(option) // the deletion manupulates the list; so we need to collect first the checked options
            }
        });
        checkedOptions.forEach((option) => {
            this.markOptionAsDeleted(option.index)
        })
    }

    public markOptionAsDeleted(optionIndex: number) {
        const option = this.getByOptionIndex(optionIndex)
        if (option) {
            option.isDeleted = true
            this.removeFromSortMap(option.index)
        }

    }

    private removeFromSortMap(optionIndex: number) {
        const indexInSortMap = this._sortMap.indexOf(optionIndex)
        if (indexInSortMap !== -1) {
            this._sortMap.splice(indexInSortMap, 1)
        }
    }

    public add(optionToBeAdded: OptionClass, selectedOptionIndex?: number) {
        let selectedOption: OptionClass
        if (selectedOptionIndex) {
            selectedOption = this.getByOptionIndex(selectedOptionIndex)
        }
        optionToBeAdded.index = this._optionClassArray.length // make sure the optionIndex is correct
        this._optionClassArray.push(optionToBeAdded)
        this.addToSortMap(optionToBeAdded, this.getIndexToBeAddedAt(optionToBeAdded, selectedOption))
    }

    private addToSortMap(optionClass: OptionClass, indexToBeAddedAt: number) {
        if (indexToBeAddedAt > this._sortMap.length + 1) { throw new Error('addToSortMap indextToBeAddedAt > this._sortMap.length + 1') }
        const sortMapWithoutElement = this._sortMap
        const newSortMap = sortMapWithoutElement.slice(0, indexToBeAddedAt)
            .concat([optionClass.index])
            .concat(sortMapWithoutElement.slice(indexToBeAddedAt));
        this._sortMap = newSortMap
    }



    private hasChildOptions(option: OptionClass) {
        console.warn('hasChildOptions X', option)
        if (!option.optionConfig.isParent) return false;
        // get next option and check if that is a parent
        const nextOption = this.getNextOption(option)
        if (!nextOption) {
            console.warn('hasChildOptions !nextOptions')
            return false
        }
        if (nextOption.optionConfig.isParent) {
            console.warn('hasChildOptions false')
            return false
        } else {
            console.warn('hasChildOptions true')
            return true
        }
    }

    private getIsChild(option: OptionClass): boolean {
        const parent = this.getParentOption(option)
        if (parent) return true
        return false
    }

    private move(optionClass: OptionClass, indexToBeMovedAt: number) {
        if (indexToBeMovedAt > this._sortMap.length + 1) { throw new Error('moveOption indextToBeAddedAt > this._sortMap.length + 1') }
        const oldSortIndex = this.getSortIndex(optionClass.index)
        const firstPiece = this.sortMap.slice(0, oldSortIndex)
        const secondPiece = this.sortMap.slice(oldSortIndex + 1)
        const sortMapWithoutElement = firstPiece.concat(secondPiece)
        const newSortMap = sortMapWithoutElement.slice(0, indexToBeMovedAt)
            .concat([optionClass.index])
            .concat(sortMapWithoutElement.slice(indexToBeMovedAt));
        this._sortMap = newSortMap
    }


    private getIndexToBeAddedAt(optionToBeAdded: OptionClass, selectedOption: OptionClass) {
        let indextToBeAddedAt: number
        if (selectedOption) {// there is a selectedOption
            if (!optionToBeAdded.isOfQuestionTypeDate) {
                // text   put it behind selectedOption
                indextToBeAddedAt = this.getSortIndex(selectedOption.index) + 1
            }
            else {// date-option
                if (this.willbeChild(optionToBeAdded, selectedOption)) {
                    // new opton will have a parent: pot behind the selected one
                    indextToBeAddedAt = this.getSortIndex(selectedOption.index) + 1
                }
                else { // will not be a child of another option
                    indextToBeAddedAt = this.newSortIndexforDateOption(optionToBeAdded)
                }
            }
        }
        else { // no selectedOption
            if (!optionToBeAdded.isOfQuestionTypeDate) { // // text or textAndNumber-Option
                indextToBeAddedAt = this.newSortIndexforTextOption(optionToBeAdded)
            }
            else { // date-option
                indextToBeAddedAt = this.newSortIndexforDateOption(optionToBeAdded)
            }
        }
        return indextToBeAddedAt;
    }

    /**
     *
     * @param newSortIndexforDateOption
     * @returns index behind the last date-option in sort-map that is smaller than optionToBeAdded
     * if no date-option yet in list: at the beginning
     */
    private newSortIndexforDateOption(dateOptionToBeAdded: OptionClass): number {
        if (!dateOptionToBeAdded.isOfQuestionTypeDate) throw new Error('newSortIndexforDateOption: no date-option')
        let newIndex = 0
        //  behind the last date-option in sort-map that is smaller than optionToBeAdded
        const latestOptionBefore = this.getLastBeforeDate(dateOptionToBeAdded.timePeriodClass.startDateTime);
        if (latestOptionBefore) {
            newIndex = this.getSortIndex(latestOptionBefore.index) + 1;
        }
        else { //if no date-option yet in list put it on first position
            newIndex = 0;
        }
        return newIndex
    }

    /**
     *
     * @param optionToBeAdded
     * @param selectedOption
     * @returns true if the new option will have a parent
     */
    private willbeChild(optionToBeAdded: OptionClass, selectedOption: OptionClass): boolean {
        if (optionToBeAdded.optionConfig.isParent) return false;
        if (selectedOption.optionConfig.isParent) return true;
        if (this.getParentOption(selectedOption)) return true
        return false
    }


    /**
     *
     * @param newSortIndexforTextOption
     * @returns  if there is a textOption already put it behind the last text option
     * else put it behind the last (last in sort-map) date-option
     */
    private newSortIndexforTextOption(textOptionToBeAdded: OptionClass): number {
        let newIndex = 0
        if (textOptionToBeAdded.isOfQuestionTypeDate) throw new Error('newSortIndexforDateOption: no text-option')
        const lastTextOption = this.lastTextOption
        if (lastTextOption) {
            newIndex = this.getSortIndex(lastTextOption.index) + 1
        } else {
            const lastDateOption = this.lastDateOption
            if (lastDateOption) {
                newIndex = this.getSortIndex(lastDateOption.index) + 1
            } else {
                newIndex = 0
            }
        }
        return newIndex
    }

    public getBySortIndex(sortIndex: number): OptionClass {
        // return this.orderedOptions[sortIndex] // wrong becanuse the excludes the deleted optioons
        const optionIndex = this.getOptionIndex(sortIndex)
        return this._optionClassArray[optionIndex]
    }

    public getByOptionIndex(optionIndex: number): OptionClass {
        return this._optionClassArray[optionIndex]
    }

    getSortIndex(optionIndex: number): number {
        for (let i = 0; i < this._sortMap.length; i++) {
            if (this._sortMap[i] === optionIndex) { return i }
        }
        throw (new Error('getSortIndex no such option in sortMap ' + optionIndex + "; " + JSON.stringify(this._optionClassArray) + "; " + JSON.stringify(this._sortMap)))
    }

    getOptionIndex(sortIndex: number): number {
        if (this._sortMap[sortIndex] === null) throw (new Error('!this._sortMap[sortIndex]'))
        return this._sortMap[sortIndex]
    }

    get dateOptions(): OptionClass[] {
        const returnValue: OptionClass[] = []
        this.forEach(option => {
            if (option.isOfQuestionTypeDate) {
                returnValue.push(option)
            }
        })
        return returnValue
    }

    get textOptions(): OptionClass[] {
        const returnValue: OptionClass[] = []
        this.forEach(option => {
            if (option.isOfQuestionTypeText) {
                returnValue.push(option)
            }
        })
        return returnValue
    }

    get allOptions(): OptionClass[] {
        const returnValue: OptionClass[] = []
        this.forEach(option => {
            returnValue.push(option)
        })
        return returnValue
    }

    /**
     *
     * @param option should be in the OptionList
     * @returns the next non-deleted option in the sort order - null if ther is none
     */

    getNextOption(option: OptionClass): OptionClass {
        // Start from the next option and find the first non-deleted option
        for (let sortIndex = this.getSortIndex(option.index) + 1; sortIndex < this.sortMap.length; sortIndex++) {
            const nextOption = this.getBySortIndex(sortIndex)
            if (!nextOption.isDeleted) {
                return nextOption;
            }
        }
        return null;
    }

    /**
     *
     * @param option that must be of this list
     * @returns true if option.index is the last optionIndex in sortMap that is not marked isDeleted
     */
    isLast(option: OptionClass): boolean {
        const lastOption = this.lastOption
        if (lastOption === null) return false // 'There is no non-deleted option in this list so its not the last
        return this.lastOption.index === option.index
    }

    /**
     * get the last non deleted option
     */
    get lastOption(): OptionClass {
        let returnValue: OptionClass = null;
        this.forEach(option => { // for each is iterating in sort order and jumps deleted options
            returnValue = option
        })
        return returnValue
    }

    /**
     *
     * @returns null if there is no text opption
     */
    get lastTextOption(): OptionClass {
        let returnValue: OptionClass = null;
        this.forEach(option => { // for each is iterating in sort order and jumps deleted options
            if (option.isOfQuestionTypeText) {
                returnValue = option
            }
        })
        return returnValue
    }

    /**
    *
    * @returns null if there is no date option
    */
    get lastDateOption(): OptionClass {
        let returnValue: OptionClass = null;
        this.forEach(option => { // for each is iterating in sort order and jumps deleted options
            if (option.isOfQuestionTypeDate) {
                if (!returnValue ||
                    returnValue.timePeriodClass.startDateTime < option.timePeriodClass.startDateTime
                ) {
                    returnValue = option
                }
            }
        })
        return returnValue
    }

    /**
    * @returns returns the latest date of all dates in OptionList
    */
    get latestDateOption(): OptionClass {
        let returnValue: OptionClass = null;
        this.forEach(option => { // forEach is iterating in sort order and jumps deleted options
            if (option.isOfQuestionTypeDate) {
                if (returnValue === null || option.timePeriodClass.startDateTime > returnValue.timePeriodClass.startDateTime) {
                    returnValue = option
                }
            }
        })
        return returnValue
    }

    resetLastSave() {
        this.resetSortMapLastSave()
        this.resetOptionsLastSave()
    }

    resetOptionsLastSave() {
        this._optionsSinceLastSave = []
        this._optionClassArray.forEach(option => {
            this._optionsSinceLastSave.push(option.deepCopy())
        })
    }

    resetLastSaveForOneOption(option: OptionClass) {
        const optionIndex = option.index
        this._optionsSinceLastSave[optionIndex] = option.deepCopy()

    }

    resetSortMapLastSave() {
        if (this._sortMap) {
            this._sortMapSinceLastSave = [...this._sortMap]
        }
    }

    get sortMapHasChangedSinceLastSave(): boolean {
        return !isEqual(this._sortMap, this._sortMapSinceLastSave);
    }

    public optionHasChangedSinceLastSave(optionIndex: number): boolean {
        if (this._optionClassArray[optionIndex] && this._optionsSinceLastSave[optionIndex]) {
            return !isEqual(this._optionClassArray[optionIndex], this._optionsSinceLastSave[optionIndex]);
        } else {
            return true;
        }
    }



    /**
       * @returns new timeperiod object with the date and time depending on what was added to the list before
       * date (if not provided) is set to the date of the last date option
       * time is taken from the date option  and if it's the same date we add on hour
       *
       */
    public getTimePeriodForNewOption(date?: Date): TimePeriod {




        console.warn('getTimePeriodForNewOption', date)
        if (this.dateOptions.length === 0) {
            return TimePeriod.newFromJSDate(date)
        }
        let addOneHour = true;
        {// first fix the date
            if (!date) {
                addOneHour = false; // if there is no date provided take the same date that was added before
                date = this.lastDateOption?.timePeriodClass.startDateTime.toJSDate();
            }
        }
        {// fix the time
            let startTimeString: string;
            let endTimeString: string;
            const lastOptionOfDate = this.getLatestOptionOfSameDate(date);
            if (lastOptionOfDate) {
                // if yes: get the last option of the same date and add on hour
                startTimeString = lastOptionOfDate.timePeriodClass.startTimeString;
                endTimeString = lastOptionOfDate.timePeriodClass.endTimeString
                if (addOneHour) {
                    console.warn('addOneHour', startTimeString, endTimeString)
                    startTimeString = TimePeriod.addHour(startTimeString)
                    endTimeString = TimePeriod.addHour(endTimeString)
                    console.warn('addOneHour new', startTimeString, endTimeString)

                }
            } else {
                const dateOptionAddedLast = this.getDateOptionAddedLast();
                if (dateOptionAddedLast != null) {
                    startTimeString = dateOptionAddedLast.timePeriodClass.startTimeString;
                    endTimeString = dateOptionAddedLast.timePeriodClass.endTimeString;
                }
            }
            return TimePeriod.newFromJSDate(date, startTimeString, endTimeString)
        }
    }

    /**
     * @attention make sure all new option persist after calling this
     * @param option
     * @param dates
     */
    public repeatDateOption(option: OptionClass, dates: Date[]) {
        if (!option.isOfQuestionTypeDate) throw new Error('repeatOption not a date option' + JSON.stringify(option))
        dates.forEach((date) => {
            const newOption = option.deepCopy()
            if (newOption.timePeriodClass.isMultiDay) {
                const durationDays = Math.round(
                    newOption.timePeriodClass.endDateTime.diff(newOption.timePeriodClass.startDateTime, 'days').days
                );
                newOption.timePeriodClass.setStartDate(date);
                const endDate = DateTime.fromJSDate(date).plus({ days: durationDays }).toJSDate();
                newOption.timePeriodClass.setEndDate(endDate);
            } else {
                newOption.timePeriodClass.setStartAndEndDate(date)
            }
            this.add(newOption)
        });
    }

    /**
     *
     * @param date
     * @returns null if there is no option of the same date
     */
    private getLatestOptionOfSameDate(date: Date): OptionClass {
        let returnValue: OptionClass = null;
        this.forEach(option => { // forEach is iterating in sort order and jumps deleted options
            if (option.isOfQuestionTypeDate) {

                if (areDatesEqual(option.timePeriodClass.startDateTime, DateTime.fromJSDate(date))) {
                    if (returnValue === null ||
                        option.timePeriodClass.startDateTime > returnValue.timePeriodClass.startDateTime // startDate actually does consider the time
                    ) {
                        returnValue = option
                    }
                }
            }
        })
        return returnValue
        function areDatesEqual(date1: DateTime, date2: DateTime): boolean {
            return date1.setZone(date1.zoneName).hasSame(date2.setZone(date2.zoneName), 'day');
        }
    }


    private getDateOptionAddedLast() {
        let returnValue: OptionClass = null;
        this.forEach(option => { // forEach is iterating in sort order and jumps deleted options
            if (option.isOfQuestionTypeDate) {
                if (returnValue === null || option.index > returnValue.index) {
                    returnValue = option
                }
            }
        })
        return returnValue
    }

    /**
     *
     * @param date
     * @returns last date-option in sort-map that is smaller than optionToBeAdded - null if there is no date
     */
    private getLastBeforeDate(date: DateTime): OptionClass {
        let returnValue: OptionClass = null;
        this.forEach(option => {
            if (option.isOfQuestionTypeDate) {
                if (option.timePeriodClass.startDateTime < date) {
                    returnValue = option
                }
            }
        })
        return returnValue
    }


    private getLatestOptionBeforeDate(date: DateTime): OptionClass {
        let returnValue: OptionClass = null;
        this.forEach(option => { // forEach is iterating in sort order and jumps deleted options
            if (option.isOfQuestionTypeDate) {
                if (option.timePeriodClass.startDateTime < date) {
                    if (returnValue === null || option.timePeriodClass.startDateTime > returnValue.timePeriodClass.startDateTime) {
                        returnValue = option
                    }
                }
            }
        })
        return returnValue
    }

    public isPreviousOfSameStartDateTime(optionClass: OptionClass): boolean {
        if (!optionClass.isOfQuestionTypeDate) { return false }
        const sortIndex = this.getSortIndex(optionClass.index)
        if (sortIndex === 0) { return false }
        const previousOptionIndex = this.sortMap[sortIndex - 1]
        const previousOption = this._optionClassArray[previousOptionIndex]
        if (!previousOption || !previousOption.isOfQuestionTypeDate) return false
        return previousOption.timePeriodClass.startDateTime === optionClass.timePeriodClass.startDateTime

    }

    public isNextOfSameStartDateTime(optionClass: OptionClass): boolean {
        if (!optionClass.isOfQuestionTypeDate) { return false }
        const sortIndex = this.getSortIndex(optionClass.index)
        if (sortIndex === 0) { return false }
        const nextOptionIndex = this.sortMap[sortIndex + 1]
        const nextOption = this._optionClassArray[nextOptionIndex]
        if (!nextOption || !nextOption.isOfQuestionTypeDate) return false
        return nextOption.timePeriodClass.startDateTime === optionClass.timePeriodClass.startDateTime
    }

    public getParentOption(option: OptionClass): OptionClass | null {
        if (!option || !option.optionConfig || option.optionConfig.isParent) return null;
        const sortIndex = this.getSortIndex(option.index);
        for (let i = sortIndex - 1; i >= 0; i--) {
            const optionIndex = this.sortMap[i];
            const potentialParentOption = this._optionClassArray[optionIndex];
            if (potentialParentOption?.optionConfig.isParent) {
                return potentialParentOption;
            }
        }
        return null;
    }

    get isParentInList(): boolean {
        let returnValue = false
        this.forEach(option => {
            if (option.optionConfig.isParent) returnValue = true
        })
        return returnValue

    }

}
